import { useChatStore } from "@/store/chat";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { useHandlerCompanionEnabled } from "@/features/stepper/steptypes/companion-config";
import type { Identity } from "../types";

export function useHandlerSelection(identity: Identity) {
  const enabled = useHandlerCompanionEnabled();
  const client = useQueryClient();
  useEffect(() => {
    const refresh = async (event: Event) => {
      useChatStore.getState().setHandlerTip(true);
      const handler = (event as CustomEvent<string>).detail;
      if (typeof handler === "string" && handler) {
        try {
          await apiClient.post(API_ENDPOINTS.companion.open, { handler });
        } catch {
          /* The journey also saves selection; a later refresh can restore it. */
        }
      }
      void client.invalidateQueries({
        queryKey: ["webchat", "handler-selection"],
      });
      void client.invalidateQueries({
        queryKey: ["webchat", identity.clientId, identity.memberId],
      });
    };
    window.addEventListener("sm:handler-introduced", refresh);
    return () => window.removeEventListener("sm:handler-introduced", refresh);
  }, [client, identity.clientId, identity.memberId]);
  return useQuery({
    queryKey: [
      "webchat",
      "handler-selection",
      identity.clientId,
      identity.memberId,
      identity.adventureId,
    ],
    enabled,
    queryFn: async ({ signal }) =>
      (
        await apiClient.get<{
          result: { handler: string | null; chatId: string | null };
        }>(API_ENDPOINTS.companion.open, { signal })
      ).data.result,
    meta: { private: true },
    retry: false,
  });
}
